import * as THREE from 'three';
import blackHoleVert from '../shaders/blackHole.vert?raw';
import blackHoleFrag from '../shaders/blackHole.frag?raw';
import { createGlowTexture } from '../utils/createGlowTexture.js';

const SKY_RADIUS = 800;

// Soft circular glow texture for the infall particles - turns the
// otherwise square Points into round dots
const PARTICLE_GLOW = createGlowTexture('255, 200, 130');

function azAlt(azDeg, altDeg) {
  const az = (azDeg * Math.PI) / 180;
  const alt = (altDeg * Math.PI) / 180;
  const cosAlt = Math.cos(alt);
  return new THREE.Vector3(
    SKY_RADIUS * cosAlt * Math.sin(az),
    SKY_RADIUS * Math.sin(alt),
    -SKY_RADIUS * cosAlt * Math.cos(az),
  );
}

const PARTICLE_COUNT = 80;
const SPAWN_RADIUS_MIN = 110;
const SPAWN_RADIUS_MAX = 220;
const EVENT_HORIZON_LOCAL = 28;

/**
 * BlackHole - visual centerpiece. EHT M87 style: face-on glowing donut +
 * opaque event horizon sphere + particles being pulled inward.
 *
 * Three parts:
 *  1. Event horizon sphere (small, opaque black, occludes background stars)
 *  2. Accretion disk - billboard quad with shader (orange ring + Doppler
 *     crescent + scrolling turbulence + breathing pulse + rotating hot spot)
 *  3. Infall particles - points spawned in a shell around the black hole
 *     that drift inward, accelerating as they get closer, and respawn at
 *     the outer shell when they reach the event horizon. Gives the visual
 *     of matter being eaten.
 *
 * All animation is local - the black hole stays at a fixed position in the
 * sky and doesn't move.
 */
export class BlackHole {
  constructor({ onClick } = {}) {
    this._t = 0;
    this.id = 'black-hole';
    this.discoveryId = null; // not in the discovery checklist
    this.interactable = true;
    this.onClickCallback = onClick;

    this.mesh = new THREE.Group();
    const pos = azAlt(180, -75);
    this.mesh.position.copy(pos);

    // === Event horizon sphere ===
    const sphereGeometry = new THREE.SphereGeometry(EVENT_HORIZON_LOCAL, 32, 32);
    const sphereMaterial = new THREE.MeshBasicMaterial({
      color: 0x000000,
      depthWrite: true,
    });
    this.eventHorizon = new THREE.Mesh(sphereGeometry, sphereMaterial);
    this.eventHorizon.renderOrder = 6;
    this.mesh.add(this.eventHorizon);

    // === Accretion disk ===
    const planeSize = 130;
    const planeGeometry = new THREE.PlaneGeometry(planeSize, planeSize);

    this.diskMaterial = new THREE.ShaderMaterial({
      vertexShader: blackHoleVert,
      fragmentShader: blackHoleFrag,
      transparent: true,
      side: THREE.DoubleSide,
      depthWrite: false,
      depthTest: false,
      blending: THREE.AdditiveBlending,
      uniforms: {
        uTime: { value: 0 },
      },
    });

    this.disk = new THREE.Mesh(planeGeometry, this.diskMaterial);
    this.disk.renderOrder = 5;
    this.disk.lookAt(pos.clone().multiplyScalar(2));
    this.mesh.add(this.disk);

    // Invisible click target - a separate top-level sphere mesh, NOT
    // parented under the BH Group. Sky.add will add it to sky.group as a
    // sibling. World position set explicitly to the BH position. This
    // pattern (top-level sphere) is the most reliable for raycasting and
    // sidesteps the issues we hit with deeply-nested mesh raycasting.
    const clickGeometry = new THREE.SphereGeometry(85, 16, 16);
    const clickMaterial = new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: 0,
      depthWrite: false,
      depthTest: false,
    });
    this.clickTarget = new THREE.Mesh(clickGeometry, clickMaterial);
    this.clickTarget.userData.star = this;
    this.clickTarget.position.copy(pos);
    this.clickTarget.renderOrder = 7;
    // Note: NOT added to this.mesh - Sky.add will add it as a top-level
    // sibling of the BH Group based on the clickTarget pattern.

    // === Infall particles ===
    const positions = new Float32Array(PARTICLE_COUNT * 3);
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      this._spawnParticle(i, positions);
    }

    this.particleGeometry = new THREE.BufferGeometry();
    this.particleGeometry.setAttribute(
      'position',
      new THREE.BufferAttribute(positions, 3),
    );

    this.particleMaterial = new THREE.PointsMaterial({
      color: 0xffb070,
      size: 4.5,
      map: PARTICLE_GLOW,
      alphaMap: PARTICLE_GLOW,
      sizeAttenuation: true,
      transparent: true,
      opacity: 0.85,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    this.particles = new THREE.Points(this.particleGeometry, this.particleMaterial);
    this.particles.renderOrder = 4;
    this.mesh.add(this.particles);
  }

  /**
   * Show or hide the entire black hole. Used by Game to keep it hidden
   * until the visitor has made enough discoveries (the BH is the climax,
   * not a starting object). When invisible, three.js raycasting also
   * skips it, so it's not clickable either.
   */
  setHidden(hidden) {
    this.mesh.visible = !hidden;
    if (this.clickTarget) this.clickTarget.visible = !hidden;
  }

  onHover() {
    document.body.classList.add('star-hover');
  }

  onUnhover() {
    document.body.classList.remove('star-hover');
  }

  onClick() {
    if (this.onClickCallback) this.onClickCallback();
  }

  /**
   * Place a single particle at a random point on a spherical shell at a
   * random distance in [SPAWN_RADIUS_MIN, SPAWN_RADIUS_MAX) from the
   * black hole's center (in local coordinates).
   */
  _spawnParticle(i, positions) {
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(2 * Math.random() - 1);
    const r = SPAWN_RADIUS_MIN + Math.random() * (SPAWN_RADIUS_MAX - SPAWN_RADIUS_MIN);
    positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
    positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
    positions[i * 3 + 2] = r * Math.cos(phi);
  }

  update(dt) {
    this._t += dt;
    this.diskMaterial.uniforms.uTime.value = this._t;

    // Drag particles inward
    const positions = this.particleGeometry.attributes.position.array;
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      let x = positions[i * 3];
      let y = positions[i * 3 + 1];
      let z = positions[i * 3 + 2];
      const r = Math.sqrt(x * x + y * y + z * z);

      // Inside event horizon → respawn far out
      if (r < EVENT_HORIZON_LOCAL) {
        this._spawnParticle(i, positions);
        continue;
      }

      // Speed increases as particle gets closer (loose inverse-radius law).
      // Tuned so particles take ~5-15 seconds to fall in from spawn distance.
      const speed = 200 / Math.max(r, 30);
      const factor = (speed * dt) / r;

      positions[i * 3]     = x - x * factor;
      positions[i * 3 + 1] = y - y * factor;
      positions[i * 3 + 2] = z - z * factor;
    }
    this.particleGeometry.attributes.position.needsUpdate = true;
  }
}
